import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { applyTheme } from '../../themes/applyTheme';
import { buildCustomTheme } from '../../themes/customTheme';
import { DecorationRenderer } from '../../themes/decoration/DecorationRenderer';
import type { ThemeRecipe } from '../../themes/recipe';
import { THEMES } from '../../themes/registry';
import { useCustomThemes, useSyncedStore, deleteCustomTheme } from '../../store/synced';
import type { CustomTheme, Theme } from '../../themes/types';
import { ThemePickerCardMenu } from './ThemePickerCardMenu';

// Theme picker modal per plan/03 §Theme picker UX.
//
// Selection is *live previewed* on :root via applyTheme: tapping a card
// rewrites the CSS vars so the rest of the screen shows what the kid
// would see. "Käytä" (Apply) commits the choice — either a preset key or
// a custom recipe — to the profile; "Peruuta" (Cancel) closes without
// committing. The applyTheme cleanup restores the previous values on
// unmount, so the settings screen returns to its untouched look in
// either case.

export type ThemePickerSelection =
  | { kind: 'preset'; key: string }
  | { kind: 'custom'; recipe: ThemeRecipe }
  | { kind: 'customLibrary'; id: string };

export function ThemePicker({
  currentKey,
  currentRecipe,
  onApply,
  onCancel,
}: Readonly<{
  currentKey: string;
  currentRecipe?: ThemeRecipe;
  onApply: (next: ThemePickerSelection) => void;
  onCancel: () => void;
}>) {
  const navigate = useNavigate();
  const tree = useSyncedStore((s) => s.tree);
  const setTree = useSyncedStore((s) => s.setTree);
  const customThemes = useCustomThemes();
  const [pendingDelete, setPendingDelete] = useState<CustomTheme | null>(null);
  const [selection, setSelection] = useState<ThemePickerSelection>(() => {
    const libraryHit = customThemes.find((c) => c.id === currentKey);
    if (libraryHit) return { kind: 'customLibrary', id: libraryHit.id };
    if (currentRecipe) return { kind: 'custom', recipe: currentRecipe };
    return { kind: 'preset', key: currentKey };
  });

  const previewedTheme: Theme = useMemo(() => {
    if (selection.kind === 'custom') return buildCustomTheme(selection.recipe);
    if (selection.kind === 'customLibrary') {
      const found = customThemes.find((c) => c.id === selection.id);
      return found ? buildCustomTheme(found) : (THEMES[0] as Theme);
    }
    return THEMES.find((t) => t.key === selection.key) ?? THEMES[0]!;
  }, [selection, customThemes]);

  // Live preview: apply the previewed theme to :root, restore on cleanup.
  useEffect(() => applyTheme(previewedTheme), [previewedTheme]);

  return (
    <div className="theme-picker" role="dialog" aria-label="Valitse teema">
      <DecorationRenderer decoration={previewedTheme.decoration} />
      <button
        type="button"
        className="theme-picker-cancel-zone"
        aria-label="Sulje"
        onClick={onCancel}
      />
      <div className="theme-picker-preview" aria-hidden="true">
        <div
          className="theme-picker-preview-card"
          style={{ background: previewedTheme.routineCardPalette[0] ?? '#fcd34d' }}
        >
          <span className="theme-picker-preview-emoji">☀️</span>
          <span className="theme-picker-preview-name">Aamu</span>
        </div>
      </div>
      <div className="theme-picker-sheet">
        <h2 className="theme-picker-title">Valitse teema</h2>
        <ul className="theme-picker-grid">
          {THEMES.map((t) => {
            const isSelected = selection.kind === 'preset' && selection.key === t.key;
            return (
              <li key={t.key} className="theme-picker-card-wrapper">
                <button
                  type="button"
                  className={`theme-picker-card${isSelected ? ' selected' : ''}`}
                  onClick={() => setSelection({ kind: 'preset', key: t.key })}
                  aria-pressed={isSelected}
                  aria-label={t.name}
                >
                  <span
                    className="theme-picker-thumb"
                    style={{ background: t.tokens.bodyBackground }}
                  >
                    <span
                      className="theme-picker-sample"
                      style={{ background: t.routineCardPalette[0] ?? '#fcd34d' }}
                    />
                  </span>
                  <span className="theme-picker-name">{t.name}</span>
                </button>
                <ThemePickerCardMenu
                  actions={[
                    {
                      label: 'Kopioi',
                      onSelect: () => navigate(`/settings/themes/new?fromPreset=${t.key}`),
                    },
                  ]}
                />
              </li>
            );
          })}
        </ul>
        {customThemes.length > 0 ? (
          <>
            <h3 className="theme-picker-subheading">Omat teemat</h3>
            <ul className="theme-picker-grid">
              {customThemes.map((c) => {
                const isSelected = selection.kind === 'customLibrary' && selection.id === c.id;
                return (
                  <li key={c.id} className="theme-picker-card-wrapper">
                    <button
                      type="button"
                      className={`theme-picker-card${isSelected ? ' selected' : ''}`}
                      onClick={() => setSelection({ kind: 'customLibrary', id: c.id })}
                      aria-pressed={isSelected}
                      aria-label={c.name}
                    >
                      <span className="theme-picker-thumb" style={{ background: c.recipe.base }}>
                        <span
                          className="theme-picker-sample"
                          style={{ background: c.recipe.base }}
                        />
                      </span>
                      <span className="theme-picker-name">{c.name}</span>
                    </button>
                    <ThemePickerCardMenu
                      actions={[
                        {
                          label: 'Muokkaa',
                          onSelect: () => navigate(`/settings/themes/${c.id}`),
                        },
                        {
                          label: 'Kopioi',
                          onSelect: () => navigate(`/settings/themes/new?fromCustom=${c.id}`),
                        },
                        {
                          label: 'Poista',
                          onSelect: () => setPendingDelete(c),
                        },
                      ]}
                    />
                  </li>
                );
              })}
            </ul>
          </>
        ) : (
          <p className="theme-picker-empty">Ei omia teemoja. Luo ensimmäinen +</p>
        )}
        <button
          type="button"
          className="theme-picker-new"
          onClick={() => navigate('/settings/themes/new')}
        >
          + Uusi teema
        </button>
        {pendingDelete ? (
          <div className="theme-picker-confirm" role="alertdialog">
            <p>Poista teema &ldquo;{pendingDelete.name}&rdquo;?</p>
            <button type="button" onClick={() => setPendingDelete(null)}>
              Peruuta
            </button>
            <button
              type="button"
              onClick={() => {
                if (tree) setTree(deleteCustomTheme(tree, pendingDelete.id));
                setPendingDelete(null);
              }}
            >
              Poista
            </button>
          </div>
        ) : null}
        <div className="theme-picker-actions">
          <button type="button" className="settings-button" onClick={onCancel}>
            Peruuta
          </button>
          <button type="button" className="settings-button" onClick={() => onApply(selection)}>
            Käytä
          </button>
        </div>
      </div>
    </div>
  );
}
